import { computed, reactive } from "vue";
import type { Product } from "../data/products";

export type CartLine = {
  product: Product;
  size: string;
  swatch: string;
  quantity: number;
};

type CartState = {
  open: boolean;
  lines: CartLine[];
};

const state = reactive<CartState>({
  open: false,
  lines: []
});

const keyFor = (line: { product: Product; size: string; swatch: string }) =>
  `${line.product.id}::${line.size}::${line.swatch}`;

export function useCart() {
  const subtotal = computed(() =>
    state.lines.reduce((sum, line) => sum + line.product.price * line.quantity, 0)
  );

  const count = computed(() =>
    state.lines.reduce((sum, line) => sum + line.quantity, 0)
  );

  const open = () => {
    state.open = true;
  };

  const close = () => {
    state.open = false;
  };

  const toggle = () => {
    state.open = !state.open;
  };

  const add = (product: Product, size: string, swatch: string) => {
    const id = keyFor({ product, size, swatch });
    const existing = state.lines.find(
      (line) => keyFor(line) === id
    );
    if (existing) {
      existing.quantity += 1;
    } else {
      state.lines.push({ product, size, swatch, quantity: 1 });
    }
    state.open = true;
  };

  const setQuantity = (line: CartLine, quantity: number) => {
    if (quantity <= 0) {
      remove(line);
      return;
    }
    line.quantity = quantity;
  };

  const remove = (line: CartLine) => {
    const id = keyFor(line);
    state.lines = state.lines.filter((l) => keyFor(l) !== id);
  };

  const clear = () => {
    state.lines = [];
  };

  return {
    state,
    subtotal,
    count,
    open,
    close,
    toggle,
    add,
    setQuantity,
    remove,
    clear
  };
}
